const DEBUG = process.env.NODE_ENV === 'development';

export async function withRetry<T>(
  operation: () => Promise<T>,
  maxRetries = 3,
  timeoutMs = 8000, // Reduced from 15s to 8s per attempt
  debug = DEBUG
): Promise<T> {
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    let timeoutId: NodeJS.Timeout | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error(`Operation timeout after ${timeoutMs}ms`)), timeoutMs);
    });
    
    try {
      if (debug) console.log(`🔄 Starting operation... (attempt ${retryCount + 1}/${maxRetries})`);
      
      const result = await Promise.race([
        operation(),
        timeoutPromise
      ]);
      
      if (timeoutId) clearTimeout(timeoutId);
      if (debug) console.log('✅ Operation completed successfully');
      return result;
    } catch (error) {
      if (timeoutId) clearTimeout(timeoutId);
      retryCount++;
      if (debug) console.error(`❌ Operation failed (attempt ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount < maxRetries) {
        // Exponential backoff with jitter: 1s, 2s, 4s (max 10s) + random 0-1s
        const delayMs = Math.min(1000 * Math.pow(2, retryCount - 1), 10000) + Math.random() * 1000;
        if (debug) console.log(`⏳ Retrying in ${Math.round(delayMs / 100) / 10} seconds...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      } else {
        throw error;
      }
    }
  }
  
  // This should never be reached, but TypeScript requires it
  throw new Error('Max retries exceeded');
}